import { useEffect, useState } from 'react'
import { useMembers } from '@/hooks/useMembers'
import { UserRole } from '@/types'
import { Trash2, User as UserIcon, Shield, Search, Filter, Eye } from 'lucide-react'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import MemberDetailsModal from '@/components/Admin/MemberDetailsModal'
import LoadingState from '@/components/ui/LoadingState'

export default function MembersPage() {
    const { members, loading, fetchMembers, updateMemberRole, removeMember } = useMembers()
    const [searchTerm, setSearchTerm] = useState('')
    const [roleFilter, setRoleFilter] = useState<string>('ALL')
    const [memberToRemove, setMemberToRemove] = useState<string | null>(null)
    const [selectedMember, setSelectedMember] = useState<{ memberId: string, userId: string } | null>(null)

    useEffect(() => {
        fetchMembers()
    }, [fetchMembers])

    const roles: { value: UserRole; label: string; color: string }[] = [
        { value: 'owner', label: 'Proprietário', color: 'bg-[#d4a94d]/10 text-[#d4a94d] border-[#d4a94d]/20' },
        { value: 'admin', label: 'Administrador', color: 'bg-[#001c28]/10 text-[#001c28] border-[#001c28]/20' },
        { value: 'manager', label: 'Gerente', color: 'bg-green-100 text-green-700 border-green-200' },
        { value: 'member', label: 'Membro', color: 'bg-gray-100 text-gray-700 border-gray-200' }
    ]

    const filteredMembers = members.filter(member => {
        const matchesSearch =
            member.user?.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.user?.email?.toLowerCase().includes(searchTerm.toLowerCase())
        const matchesRole = roleFilter === 'ALL' || member.role === roleFilter

        return matchesSearch && matchesRole
    })

    return (
        <div className="max-w-6xl w-full mx-auto p-6 space-y-8 page-enter">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b-2 border-[#001c28]/5">
                <div className="space-y-2">
                    <h1 className="text-3xl font-black text-[#001c28] tracking-tighter uppercase italic leading-[0.9]">Gestão de Membros</h1>
                    <p className="font-bold text-[#001c28]/80 text-sm max-w-2xl">
                        Visualize, gerencie e audite todos os usuários da sua organização.
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-[2rem] border border-[#001c28]/5 shadow-sm mb-8 flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="relative flex-1 w-full">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#001c28]/30" />
                    <input
                        type="text"
                        placeholder="Buscar por nome ou email..."
                        className="w-full bg-[#001c28]/[0.02] border-none rounded-xl pl-12 pr-4 py-3 text-sm font-bold text-[#001c28] focus:ring-2 focus:ring-[#d4a94d]/20 placeholder:text-[#001c28]/30"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="relative w-full md:w-48">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#001c28]/40" />
                    <select
                        className="w-full appearance-none bg-[#001c28]/[0.02] border-none rounded-xl pl-10 pr-8 py-3 text-xs font-black uppercase tracking-wider text-[#001c28] cursor-pointer focus:ring-2 focus:ring-[#d4a94d]/20"
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                    >
                        <option value="ALL">Todos os Cargos</option>
                        {roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                </div>
            </div>

            {loading ? (
                <LoadingState />
            ) : filteredMembers.length === 0 ? (
                <div className="py-20 text-center bg-white rounded-[2.5rem] border border-[#001c28]/5 shadow-xl shadow-[#001c28]/5">
                    <UserIcon className="w-16 h-16 mx-auto text-[#001c28]/20 mb-6" />
                    <h3 className="text-xl font-bold text-[#001c28] mb-2">Nenhum membro encontrado</h3>
                    <p className="text-[#001c28]/60 max-w-sm mx-auto text-sm">Tente ajustar os filtros de busca para encontrar quem você procura.</p>
                </div>
            ) : (
                <div className="bg-white rounded-[2.5rem] border border-[#001c28]/5 shadow-xl shadow-[#001c28]/5 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-[#001c28]/[0.02] border-b border-[#001c28]/5">
                            <tr>
                                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-[#001c28]/40">Membro</th>
                                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-[#001c28]/40">Contato</th>
                                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-[#001c28]/40">Cargo</th>
                                <th className="px-8 py-5 text-right text-[10px] font-black uppercase tracking-[0.2em] text-[#001c28]/40">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#001c28]/5">
                            {filteredMembers.map((member) => (
                                <tr key={member.id} className="group hover:bg-[#001c28]/[0.02] transition-colors">
                                    <td className="px-8 py-4">
                                        <div className="flex items-center gap-4">
                                            {member.user?.photoURL ? (
                                                <img
                                                    src={member.user.photoURL}
                                                    alt={member.user.displayName}
                                                    className="w-10 h-10 rounded-xl object-cover shadow-sm bg-gray-100"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded-xl bg-[#001c28]/10 flex items-center justify-center text-[#001c28] font-bold text-lg">
                                                    {member.user?.displayName?.charAt(0) || <UserIcon className="w-5 h-5" />}
                                                </div>
                                            )}
                                            <div>
                                                <div className="font-bold text-[#001c28] cursor-pointer hover:text-[#d4a94d] transition-colors"
                                                    onClick={() => setSelectedMember({ memberId: member.id, userId: member.userId })}>
                                                    {member.user?.displayName || 'Usuário Desconhecido'}
                                                </div>
                                                <div className="text-xs text-[#001c28]/50 font-medium">Desde {member.joinedAt?.toDate().toLocaleDateString()}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-4 text-sm font-medium text-[#001c28]/70">
                                        {member.user?.email}
                                        {member.user?.phone && <div className="text-xs text-[#001c28]/50 mt-1">{member.user.phone}</div>}
                                    </td>
                                    <td className="px-8 py-4">
                                        <div className="relative inline-block group/role">
                                            <select
                                                value={member.role}
                                                onChange={(e) => updateMemberRole(member.id, e.target.value as UserRole)}
                                                className={`appearance-none pl-3 pr-8 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider border cursor-pointer outline-none transition-all ${roles.find(r => r.value === member.role)?.color || 'bg-gray-100 text-gray-600 border-gray-200'
                                                    } border-transparent hover:border-black/10`}
                                            >
                                                {roles.map(r => (
                                                    <option key={r.value} value={r.value}>{r.label}</option>
                                                ))}
                                            </select>
                                            <Shield className="w-3 h-3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-50" />
                                        </div>
                                    </td>
                                    <td className="px-8 py-4 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => setSelectedMember({ memberId: member.id, userId: member.userId })}
                                                className="p-2 text-[#001c28]/40 hover:text-[#001c28] hover:bg-[#001c28]/5 rounded-lg transition-colors"
                                                title="Ver Detalhes"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => setMemberToRemove(member.id)}
                                                className="p-2 text-[#001c28]/40 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Remover Membro"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmDialog
                isOpen={!!memberToRemove}
                title="Remover Membro"
                description="Tem certeza que deseja remover este membro da organização? Ele perderá acesso imediatamente."
                confirmText="Remover"
                variant="danger"
                onConfirm={async () => {
                    if (memberToRemove) {
                        await removeMember(memberToRemove)
                        setMemberToRemove(null)
                    }
                }}
                onClose={() => setMemberToRemove(null)}
            />

            {/* User Details Modal */}
            <MemberDetailsModal
                isOpen={!!selectedMember}
                onClose={() => setSelectedMember(null)}
                userId={selectedMember?.userId || ''}
            />
        </div>
    )
}
